<script lang="ts">
    import {
        audioElement,
        duration,
        playbackRate,
        percentage as timelinePercentage,
        playing,
        time,
    } from "@utils/audiobook/audiobook"
    import Icon from "@components/material/Icon.svelte"
    import { dragger } from "@utils/directives/useDragger"
    import { clickEnter, sliderDirective } from "@utils/directives/accessibility"

    export let expanded = false

    const playbackRates = [0.8, 1, 1.25, 1.5]

    function timelineClick(e: PointerEvent) {
        const timelineBounds = timeline.getBoundingClientRect()
        const percentage = (e.clientX - timelineBounds.left) / timelineBounds.width
        setTimeline(percentage)
    }

    function setTimeline(percentage: number) {
        if (!audioElement.duration) return
        const timeToSeek = percentage * audioElement.duration
        audioElement.currentTime = Math.min(audioElement.duration, Math.max(0, timeToSeek))
    }

    async function playBtnClick() {
        if (audioElement.paused) {
            try {
                await audioElement.play()
            } catch (e) {
                console.warn(e)
            }
        } else {
            audioElement.pause()
        }
    }

    function back30() {
        audioElement.currentTime = Math.max(audioElement.currentTime - 30, 0)
    }

    function forward30() {
        if (!audioElement.duration) return
        audioElement.currentTime = Math.min(audioElement.currentTime + 30, audioElement.duration)
    }

    function setPlaybackRate(rate: number) {
        audioElement.playbackRate = rate
    }

    function formatRate(rate: number) {
        return `${rate}x`
    }

    let timeline: HTMLDivElement
</script>

<div class="audio-controls" class:expanded>
    <div
        class="timeline"
        bind:this={timeline}
        tabindex="0"
        aria-label="timeline"
        role="slider"
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={$timelinePercentage}
        use:sliderDirective={{
            max: 1,
            min: 0,
            valuestore: timelinePercentage,
            callback: setTimeline,
        }}
        use:dragger={{ callback: timelineClick }}
    >
        <div class="progress" style:width={`${$timelinePercentage * 100}%`} />
    </div>

    <div class="controls-row">
        {#if expanded}
            <button
                type="button"
                class="transport-button"
                tabindex="0"
                aria-label="rewind 30 seconds"
                on:keydown={clickEnter}
                on:click={back30}
            >
                <Icon icon="back30" />
            </button>
        {/if}

        <button
            type="button"
            class="transport-button play-button"
            aria-label={$playing ? "Pause audio" : "Play audio"}
            aria-pressed={$playing}
            on:keydown={clickEnter}
            on:click={playBtnClick}
        >
            <Icon icon={$playing ? "pause" : "play"} />
        </button>

        {#if expanded}
            <button
                type="button"
                class="transport-button forward30"
                aria-label="forward 30 seconds"
                on:keydown={clickEnter}
                on:click={forward30}
            >
                <Icon icon="back30" />
            </button>
        {/if}

        <div class="time">
            <div class="current" aria-label="time">{$time}</div>
            <div class="divider">/</div>
            <div class="length" aria-label="duration">{$duration}</div>
        </div>
    </div>

    {#if expanded}
        <div class="speed-row" aria-label="Playback speed">
            {#each playbackRates as rate}
                <button
                    type="button"
                    class:active={rate === $playbackRate}
                    aria-pressed={rate === $playbackRate}
                    on:click={() => setPlaybackRate(rate)}
                >
                    {formatRate(rate)}
                </button>
            {/each}
        </div>
    {/if}
</div>

<style>
    :global(audio) {
        width: 100%;
    }

    .audio-controls {
        display: grid;
        grid-template-columns: minmax(5rem, 1fr) auto;
        gap: 0.55rem;
        align-items: center;
        min-width: 8rem;
    }

    .audio-controls.expanded {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }

    .timeline {
        position: relative;
        width: 100%;
        height: 0.45rem;
        border-radius: 999px;
        background: var(--audio-player-border);
        cursor: pointer;
        overflow: hidden;
    }

    .timeline:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 3px;
    }

    .progress {
        width: 0;
        height: 100%;
        border-radius: inherit;
        background: var(--audio-player-accent);
        transition: width 0.12s linear;
    }

    .controls-row {
        display: flex;
        gap: 0.45rem;
        align-items: center;
        justify-content: flex-end;
        min-width: 0;
    }

    .expanded .controls-row {
        justify-content: center;
    }

    .transport-button {
        appearance: none;
        display: grid;
        place-items: center;
        width: 2.25rem;
        height: 2.25rem;
        border: 0;
        border-radius: 0.55rem;
        background: transparent;
        box-shadow: none;
        color: var(--audio-player-muted);
        cursor: pointer;
        padding: 0;
        transition:
            background-color 0.16s ease,
            color 0.16s ease,
            transform 0.16s ease;
    }

    .transport-button:hover {
        background: var(--audio-player-accent-soft);
        color: var(--audio-player-accent);
    }

    .transport-button:active {
        transform: translateY(1px);
    }

    .transport-button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .transport-button:disabled {
        cursor: progress;
        opacity: 0.55;
    }

    .transport-button :global(svg) {
        width: 1.2rem;
        height: 1.2rem;
    }

    .play-button {
        width: 2.55rem;
        height: 2.55rem;
        border-radius: 50%;
        background: var(--audio-player-accent);
        color: var(--audio-player-accent-text);
    }

    .play-button:hover {
        background: var(--audio-player-accent);
        color: var(--audio-player-accent-text);
    }

    .play-button :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .forward30 :global(svg) {
        transform: scaleX(-1);
    }

    .time {
        display: flex;
        gap: 0.2rem;
        min-width: 5.75rem;
        justify-content: flex-end;
        color: var(--audio-player-muted);
        font-size: 0.78rem;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        line-height: 1;
    }

    .expanded .time {
        min-width: 6.5rem;
    }

    .speed-row {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 0.35rem;
        justify-self: center;
        width: min(100%, 22rem);
        padding: 0.25rem;
        border: 1px solid var(--audio-player-border);
        border-radius: 0.5rem;
        background: var(--audio-player-surface);
    }

    .speed-row button {
        appearance: none;
        min-height: 2rem;
        border: 0;
        border-radius: 0.35rem;
        background: transparent;
        box-shadow: none;
        color: var(--audio-player-muted);
        cursor: pointer;
        font: inherit;
        font-size: 0.78rem;
        font-weight: 800;
        letter-spacing: 0;
        line-height: 1;
        padding: 0.45rem 0.35rem;
        text-transform: none;
    }

    .speed-row button:hover,
    .speed-row button.active {
        background: var(--audio-player-accent-soft);
        color: var(--audio-player-accent);
    }

    @media only screen and (max-width: 520px) {
        .audio-controls {
            grid-template-columns: auto;
            min-width: 2.55rem;
        }

        .audio-controls:not(.expanded) .timeline {
            display: none;
        }

        .audio-controls:not(.expanded) {
            gap: 0;
        }
    }
</style>
